import React, { useState, useEffect, useCallback } from 'react';
import axiosClient from '../api/axiosClient';
import {
  PageHeader, Card, Btn, Tabs, DataTable,
  StandardModal, EmptyState, StatusBadge, Alert,
  colors, spacing, typography, radius, shadows,
  inputStyle, textareaStyle, selectStyle,
} from '../design-system';

// ── Helpers ────────────────────────────────────────────────────────────────────

const TYPE_LABELS = {
  download:    'Download',
  physical:    'Merchandise',
};

const TYPE_ICONS = {
  download:    '📄',
  physical:    '🛍️',
};

const STATUS_LABELS = {
  active:   'Active',
  draft:    'Draft',
  archived: 'Archived',
};

const ORDER_STATUS_LABELS = {
  pending:       'Pending',
  payment_sent:  'Payment Sent',
  confirmed:     'Confirmed',
  fulfilled:     'Fulfilled',
  cancelled:     'Cancelled',
};

const ORDER_STATUS_COLORS = {
  pending:      '#f59e0b',
  payment_sent: '#3b82f6',
  confirmed:    '#8b5cf6',
  fulfilled:    '#10b981',
  cancelled:    '#6b7280',
};

function fmt(amount) {
  if (amount === 0) return 'Free';
  return `£${Number(amount).toFixed(2)}`;
}

function StatusChip({ status, map, colorMap }) {
  const label = map[status] || status;
  const color = colorMap ? colorMap[status] || '#6b7280' : '#6b7280';
  return (
    <span style={{
      background: color + '20',
      color,
      border: `1px solid ${color}40`,
      borderRadius: radius.pill,
      fontSize: typography.fontSizes.xs,
      fontWeight: typography.fontWeights.semibold,
      padding: '2px 10px',
    }}>
      {label}
    </span>
  );
}

const ITEM_STATUS_COLORS = { active: '#10b981', draft: '#f59e0b', archived: '#6b7280' };

// ── Item Form ──────────────────────────────────────────────────────────────────

function ItemForm({ initial, onSave, onCancel, saving }) {
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [isoStandards, setIsoStandards] = useState([]);
  const [pfProducts, setPfProducts]     = useState([]);
  const [pfVariants, setPfVariants]     = useState([]);
  const [pfLoading, setPfLoading]       = useState(false);
  const [form, setForm] = useState({
    title: '',
    description: '',
    item_type: 'download',
    price: 0,
    is_free: false,
    requires_accreditation: false,
    iso_standard_id: '',
    file_url: '',
    thumbnail_url: '',
    external_url: '',
    status: 'draft',
    stock_quantity: '',
    sort_order: 0,
    printful_sync_product_id: null,
    printful_variant_id: null,
    ...initial,
  });

  useEffect(() => {
    axiosClient.get('/iso-standards/')
      .then(r => setIsoStandards(Array.isArray(r.data) ? r.data : (r.data?.available_isos || [])))
      .catch(() => {});
  }, []);

  const [pfVariantError, setPfVariantError] = useState('');
  const [pfVariantLoading, setPfVariantLoading] = useState(false);

  // Load Printful products when item type is physical
  useEffect(() => {
    if (form.item_type !== 'physical') return;
    setPfLoading(true);
    axiosClient.get('/printful/products')
      .then(r => {
        const products = r.data?.products || [];
        setPfProducts(products);
        // Auto-fill thumbnail from the already-selected product
        if (form.printful_sync_product_id && !form.thumbnail_url) {
          const p = products.find(p => p.id === form.printful_sync_product_id);
          if (p?.thumbnail_url) set('thumbnail_url', p.thumbnail_url);
        }
      })
      .catch(() => setPfProducts([]))
      .finally(() => setPfLoading(false));
  }, [form.item_type]);

  // Load variants when a product is selected
  useEffect(() => {
    if (!form.printful_sync_product_id) { setPfVariants([]); setPfVariantError(''); return; }
    setPfVariantLoading(true);
    setPfVariantError('');
    axiosClient.get(`/printful/products/${form.printful_sync_product_id}`)
      .then(r => {
        const vs = r.data?.sync_variants || [];
        setPfVariants(vs);
        if (vs.length === 0) setPfVariantError('No variants found for this product.');
      })
      .catch(err => {
        setPfVariants([]);
        setPfVariantError(err?.response?.data?.detail || 'Failed to load variants.');
      })
      .finally(() => setPfVariantLoading(false));
  }, [form.printful_sync_product_id]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    setUploadError('');
    try {
      const token = localStorage.getItem('token');
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch(
        `${import.meta.env.VITE_API_URL || 'http://localhost:8000/api'}/shop/upload-download`,
        { method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: fd }
      );
      if (!res.ok) { const d = await res.json(); throw new Error(d.detail || 'Upload failed'); }
      const data = await res.json();
      set('file_url', data.file_url);
      // Auto-use as thumbnail if the uploaded file is an image
      if (/\.(png|jpe?g|gif|webp|svg)$/i.test(data.file_url)) {
        set('thumbnail_url', data.file_url);
      }
    } catch (err) {
      setUploadError(err.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const toInt = v => (v === '' || v === null || v === undefined) ? null : parseInt(v, 10);
    const payload = {
      ...form,
      price:                    form.is_free ? 0 : Number(form.price),
      stock_quantity:           toInt(form.stock_quantity),
      sort_order:               Number(form.sort_order) || 0,
      iso_standard_id:          toInt(form.iso_standard_id),
      printful_sync_product_id: toInt(form.printful_sync_product_id),
      printful_variant_id:      toInt(form.printful_variant_id),
    };
    onSave(payload);
  };

  return (
    <form id="item-form" onSubmit={handleSubmit}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.md }}>

        <div>
          <label style={{ display: 'block', fontSize: typography.fontSizes.sm, fontWeight: typography.fontWeights.medium, color: colors.textSecondary, marginBottom: 4 }}>Title *</label>
          <input style={inputStyle} value={form.title} onChange={e => set('title', e.target.value)} required />
        </div>

        <div>
          <label style={{ display: 'block', fontSize: typography.fontSizes.sm, fontWeight: typography.fontWeights.medium, color: colors.textSecondary, marginBottom: 4 }}>Description</label>
          <textarea style={{ ...textareaStyle, minHeight: 80 }} value={form.description || ''} onChange={e => set('description', e.target.value)} />
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: spacing.md }}>
          <div>
            <label style={{ display: 'block', fontSize: typography.fontSizes.sm, fontWeight: typography.fontWeights.medium, color: colors.textSecondary, marginBottom: 4 }}>Type *</label>
            <select style={selectStyle} value={form.item_type} onChange={e => set('item_type', e.target.value)}>
              <option value="download">Download (PDF / file)</option>
              <option value="physical">Merchandise (Print-on-Demand)</option>
            </select>
          </div>
          <div>
            <label style={{ display: 'block', fontSize: typography.fontSizes.sm, fontWeight: typography.fontWeights.medium, color: colors.textSecondary, marginBottom: 4 }}>Status</label>
            <select style={selectStyle} value={form.status} onChange={e => set('status', e.target.value)}>
              <option value="draft">Draft</option>
              <option value="active">Active</option>
              <option value="archived">Archived</option>
            </select>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: spacing.md }}>
          <div>
            <label style={{ display: 'block', fontSize: typography.fontSizes.sm, fontWeight: typography.fontWeights.medium, color: colors.textSecondary, marginBottom: 4 }}>Price (£)</label>
            <input
              style={inputStyle}
              type="number" min="0" step="0.01"
              value={form.price}
              onChange={e => set('price', e.target.value)}
              disabled={form.is_free}
            />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: typography.fontSizes.sm, fontWeight: typography.fontWeights.medium, color: colors.textSecondary, marginBottom: 4 }}>Stock (blank = unlimited)</label>
            <input
              style={inputStyle}
              type="number" min="0"
              value={form.stock_quantity === null ? '' : form.stock_quantity}
              onChange={e => set('stock_quantity', e.target.value)}
            />
          </div>
        </div>

        <div style={{ display: 'flex', gap: spacing.xl }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: spacing.sm, cursor: 'pointer', fontSize: typography.fontSizes.md, color: colors.textPrimary }}>
            <input type="checkbox" checked={form.is_free} onChange={e => set('is_free', e.target.checked)} />
            Free item
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: spacing.sm, cursor: 'pointer', fontSize: typography.fontSizes.md, color: colors.textPrimary }}>
            <input type="checkbox" checked={form.requires_accreditation} onChange={e => set('requires_accreditation', e.target.checked)} />
            Requires active accreditation
          </label>
        </div>

        {form.requires_accreditation && (
          <div>
            <label style={{ display: 'block', fontSize: typography.fontSizes.sm, fontWeight: typography.fontWeights.medium, color: colors.textSecondary, marginBottom: 4 }}>
              ISO Standard (optional — limits access to companies certified to this standard)
            </label>
            <select
              value={form.iso_standard_id || ''}
              onChange={e => set('iso_standard_id', e.target.value ? parseInt(e.target.value) : null)}
              style={{ ...selectStyle, width: '100%' }}
            >
              <option value="">— Any active certificate (no standard restriction) —</option>
              {isoStandards.map(s => (
                <option key={s.id} value={s.id}>{s.code} — {s.name}</option>
              ))}
            </select>
          </div>
        )}

        {form.item_type === 'download' && (
          <div>
            <label style={{ display: 'block', fontSize: typography.fontSizes.sm, fontWeight: typography.fontWeights.medium, color: colors.textSecondary, marginBottom: 4 }}>
              Downloadable File
            </label>
            <input
              type="file"
              onChange={handleFileUpload}
              disabled={uploading}
              style={{ display: 'block', marginBottom: spacing.xs, fontSize: typography.fontSizes.sm, color: colors.textPrimary }}
            />
            {uploading && (
              <div style={{ fontSize: typography.fontSizes.sm, color: colors.textMuted }}>Uploading...</div>
            )}
            {uploadError && (
              <div style={{ fontSize: typography.fontSizes.sm, color: colors.danger }}>{uploadError}</div>
            )}
            {form.file_url && !uploading && (
              <div style={{ fontSize: typography.fontSizes.sm, marginTop: spacing.xs }}>
                <span style={{ color: colors.textMuted }}>Stored: </span>
                <a href={form.file_url} target="_blank" rel="noreferrer" style={{ color: colors.primary }}>
                  {form.file_url.split('/').pop()}
                </a>
              </div>
            )}
          </div>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: spacing.md }}>
          <div>
            <label style={{ display: 'block', fontSize: typography.fontSizes.sm, fontWeight: typography.fontWeights.medium, color: colors.textSecondary, marginBottom: 4 }}>Thumbnail URL</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: spacing.sm }}>
              <input style={{ ...inputStyle, flex: 1 }} value={form.thumbnail_url || ''} onChange={e => set('thumbnail_url', e.target.value)} placeholder="https://..." />
              {form.thumbnail_url && (
                <img src={form.thumbnail_url} alt="thumb" style={{ width: 36, height: 36, objectFit: 'cover', borderRadius: 4, border: `1px solid ${colors.border}`, flexShrink: 0 }} />
              )}
            </div>
          </div>
          <div>
            <label style={{ display: 'block', fontSize: typography.fontSizes.sm, fontWeight: typography.fontWeights.medium, color: colors.textSecondary, marginBottom: 4 }}>External URL</label>
            <input style={inputStyle} value={form.external_url || ''} onChange={e => set('external_url', e.target.value)} placeholder="https://..." />
          </div>
        </div>

        {form.item_type === 'physical' && (
          <div style={{ border: `1px solid ${colors.border}`, borderRadius: 8, padding: spacing.md, background: colors.background }}>
            <div style={{ fontSize: typography.fontSizes.sm, fontWeight: typography.fontWeights.semibold, color: colors.textSecondary, marginBottom: spacing.sm }}>
              Printful (Print-on-Demand)
            </div>
            {pfLoading ? (
              <div style={{ fontSize: typography.fontSizes.sm, color: colors.textMuted }}>Loading Printful products…</div>
            ) : pfProducts.length === 0 ? (
              <div style={{ fontSize: typography.fontSizes.sm, color: colors.textMuted }}>
                No Printful products found — add your PRINTFUL_API_KEY and use the Sync button on the items page.
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.sm }}>
                <div>
                  <label style={{ display: 'block', fontSize: typography.fontSizes.sm, fontWeight: typography.fontWeights.medium, color: colors.textSecondary, marginBottom: 4 }}>Printful Product</label>
                  <select
                    style={selectStyle}
                    value={form.printful_sync_product_id || ''}
                    onChange={e => {
                      const v = e.target.value ? parseInt(e.target.value) : null;
                      set('printful_sync_product_id', v);
                      set('printful_variant_id', null);
                      // Auto-populate thumbnail from selected product
                      const p = pfProducts.find(p => p.id === v);
                      if (p?.thumbnail_url) set('thumbnail_url', p.thumbnail_url);
                    }}
                  >
                    <option value="">— Not linked —</option>
                    {pfProducts.map(p => (
                      <option key={p.id} value={p.id}>{p.name}</option>
                    ))}
                  </select>
                </div>
                {pfVariantLoading && (
                  <div style={{ fontSize: typography.fontSizes.sm, color: colors.textMuted }}>Loading variants…</div>
                )}
                {pfVariantError && (
                  <div style={{ fontSize: typography.fontSizes.sm, color: colors.danger }}>{pfVariantError}</div>
                )}
                {!pfVariantLoading && pfVariants.length > 0 && (
                  <div>
                    <label style={{ display: 'block', fontSize: typography.fontSizes.sm, fontWeight: typography.fontWeights.medium, color: colors.textSecondary, marginBottom: 4 }}>Variant (size / colour)</label>
                    <select
                      style={selectStyle}
                      value={form.printful_variant_id || ''}
                      onChange={e => set('printful_variant_id', e.target.value ? parseInt(e.target.value) : null)}
                    >
                      <option value="">— Select variant —</option>
                      {pfVariants.map(v => (
                        <option key={v.id} value={v.id}>{v.name}</option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        <div style={{ width: 120 }}>
          <label style={{ display: 'block', fontSize: typography.fontSizes.sm, fontWeight: typography.fontWeights.medium, color: colors.textSecondary, marginBottom: 4 }}>Sort Order</label>
          <input style={inputStyle} type="number" value={form.sort_order} onChange={e => set('sort_order', e.target.value)} />
        </div>

      </div>
    </form>
  );
}

// ── Bank Config Form ───────────────────────────────────────────────────────────

function BankConfigForm({ config, onSave, saving }) {
  const [form, setForm] = useState({ ...config });
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(form);
  };

  return (
    <form id="bank-config-form" onSubmit={handleSubmit}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.md }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: spacing.md }}>
          <div>
            <label style={{ display: 'block', fontSize: typography.fontSizes.sm, fontWeight: typography.fontWeights.medium, color: colors.textSecondary, marginBottom: 4 }}>Bank Name</label>
            <input style={inputStyle} value={form.bank_name || ''} onChange={e => set('bank_name', e.target.value)} />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: typography.fontSizes.sm, fontWeight: typography.fontWeights.medium, color: colors.textSecondary, marginBottom: 4 }}>Account Name</label>
            <input style={inputStyle} value={form.account_name || ''} onChange={e => set('account_name', e.target.value)} />
          </div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: spacing.md }}>
          <div>
            <label style={{ display: 'block', fontSize: typography.fontSizes.sm, fontWeight: typography.fontWeights.medium, color: colors.textSecondary, marginBottom: 4 }}>Sort Code</label>
            <input style={inputStyle} value={form.sort_code || ''} onChange={e => set('sort_code', e.target.value)} placeholder="00-00-00" />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: typography.fontSizes.sm, fontWeight: typography.fontWeights.medium, color: colors.textSecondary, marginBottom: 4 }}>Account Number</label>
            <input style={inputStyle} value={form.account_number || ''} onChange={e => set('account_number', e.target.value)} />
          </div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: spacing.md }}>
          <div>
            <label style={{ display: 'block', fontSize: typography.fontSizes.sm, fontWeight: typography.fontWeights.medium, color: colors.textSecondary, marginBottom: 4 }}>IBAN</label>
            <input style={inputStyle} value={form.iban || ''} onChange={e => set('iban', e.target.value)} />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: typography.fontSizes.sm, fontWeight: typography.fontWeights.medium, color: colors.textSecondary, marginBottom: 4 }}>BIC/Swift</label>
            <input style={inputStyle} value={form.bic_swift || ''} onChange={e => set('bic_swift', e.target.value)} />
          </div>
        </div>
        <div>
          <label style={{ display: 'block', fontSize: typography.fontSizes.sm, fontWeight: typography.fontWeights.medium, color: colors.textSecondary, marginBottom: 4 }}>Bank Address</label>
          <input style={inputStyle} value={form.bank_address || ''} onChange={e => set('bank_address', e.target.value)} />
        </div>
        <div>
          <label style={{ display: 'block', fontSize: typography.fontSizes.sm, fontWeight: typography.fontWeights.medium, color: colors.textSecondary, marginBottom: 4 }}>Payment Reference Prefix</label>
          <input style={{ ...inputStyle, maxWidth: 160 }} value={form.payment_reference_prefix || ''} onChange={e => set('payment_reference_prefix', e.target.value)} placeholder="BOSS" />
        </div>
        <div>
          <label style={{ display: 'block', fontSize: typography.fontSizes.sm, fontWeight: typography.fontWeights.medium, color: colors.textSecondary, marginBottom: 4 }}>Checkout Instructions (shown to buyer)</label>
          <textarea style={{ ...textareaStyle, minHeight: 80 }} value={form.checkout_instructions || ''} onChange={e => set('checkout_instructions', e.target.value)} placeholder="Please transfer the total amount to the account above using your order number as the payment reference." />
        </div>
        <div>
          <Btn type="submit" form="bank-config-form" variant="primary" size="md" loading={saving}>Save Bank Details</Btn>
        </div>
      </div>
    </form>
  );
}

// ── Order Detail Modal ─────────────────────────────────────────────────────────

function OrderDetailModal({ order, onClose, onStatusChange }) {
  const [status, setStatus]     = useState(order.status);
  const [adminNotes, setAdminNotes] = useState(order.admin_notes || '');
  const [paidAmount, setPaidAmount] = useState(order.paid_amount ?? order.total_amount);
  const [paymentRef, setPaymentRef] = useState(order.payment_reference || '');
  const [confirmMismatch, setConfirmMismatch] = useState(false);
  const [saving, setSaving]     = useState(false);
  const [msg, setMsg]           = useState(null);

  const confirmingPayment = status === 'confirmed' && order.status !== 'confirmed';
  const [shipping, setShipping] = useState({
    name:         order.shipping_name || '',
    address1:     order.shipping_address1 || '',
    address2:     order.shipping_address2 || '',
    city:         order.shipping_city || '',
    zip:          order.shipping_zip || '',
    phone:        order.shipping_phone || '',
    country_code: order.shipping_country_code || 'GB',
  });
  const setS = (k, v) => setShipping(p => ({ ...p, [k]: v }));

  const hasPhysical = order.items?.some(i => i.item_type === 'physical' || i.item_type === 'merchandise');

  const save = async () => {
    setSaving(true);
    try {
      const res = await axiosClient.patch(`/shop/orders/${order.id}/status`, {
        status,
        admin_notes: adminNotes,
        ...(confirmingPayment && {
          paid_amount: parseFloat(paidAmount),
          payment_reference: paymentRef,
          confirm_mismatch: confirmMismatch,
        }),
        ...(hasPhysical && {
          shipping_name:         shipping.name,
          shipping_address1:     shipping.address1,
          shipping_address2:     shipping.address2,
          shipping_city:         shipping.city,
          shipping_zip:          shipping.zip,
          shipping_phone:        shipping.phone,
          shipping_country_code: shipping.country_code,
        }),
      });
      onStatusChange(res.data);
      setMsg({ type: 'success', text: 'Order updated.' });
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (confirmingPayment && typeof detail === 'string' && /does not match/i.test(detail)) {
        setConfirmMismatch(true);
        setMsg({ type: 'error', text: `${detail} Click Save Changes again to record it anyway.` });
      } else {
        setMsg({ type: 'error', text: typeof detail === 'string' ? detail : 'Failed to update order.' });
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <StandardModal
      title={`Order ${order.order_number}`}
      onClose={onClose}
      width={560}
      footer={
        <div style={{ display: 'flex', gap: spacing.sm, justifyContent: 'flex-end' }}>
          <Btn variant="secondary" size="md" onClick={onClose}>Close</Btn>
          <Btn variant="primary" size="md" onClick={save} loading={saving}>Save Changes</Btn>
        </div>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.md }}>
        {msg && <Alert type={msg.type} message={msg.text} />}

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: spacing.sm, fontSize: typography.fontSizes.sm }}>
          <div><span style={{ color: colors.textMuted }}>Customer: </span><strong>{order.customer_name}</strong></div>
          <div><span style={{ color: colors.textMuted }}>Email: </span>{order.customer_email}</div>
          <div><span style={{ color: colors.textMuted }}>Total: </span><strong>{fmt(order.total_amount)}</strong></div>
          <div><span style={{ color: colors.textMuted }}>Placed: </span>{new Date(order.created_at).toLocaleDateString()}</div>
          {order.payment_reference && (
            <div style={{ gridColumn: '1 / -1' }}>
              <span style={{ color: colors.textMuted }}>Payment Ref: </span>
              <code style={{ background: colors.background, padding: '2px 6px', borderRadius: radius.sm }}>{order.payment_reference}</code>
            </div>
          )}
          {order.paid_amount != null && (
            <div style={{ gridColumn: '1 / -1' }}>
              <span style={{ color: colors.textMuted }}>Amount Received: </span>
              <strong>{fmt(order.paid_amount)}</strong>
              {Math.abs(order.paid_amount - order.total_amount) > 0.01 && (
                <span style={{ color: '#d97706', marginLeft: 6 }}>(mismatch vs. total)</span>
              )}
            </div>
          )}
        </div>

        <div>
          <div style={{ fontSize: typography.fontSizes.sm, fontWeight: typography.fontWeights.semibold, marginBottom: spacing.sm, color: colors.textSecondary }}>Items</div>
          {order.items.map((oi, i) => (
            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: `${spacing.xs} 0`, borderBottom: `1px solid ${colors.border}`, fontSize: typography.fontSizes.sm }}>
              <span>{TYPE_ICONS[oi.item_type]} {oi.title} × {oi.quantity}</span>
              <span style={{ fontWeight: typography.fontWeights.semibold }}>{fmt(oi.unit_price * oi.quantity)}</span>
            </div>
          ))}
        </div>

        {hasPhysical && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.sm }}>
            <div style={{ fontSize: typography.fontSizes.sm, fontWeight: typography.fontWeights.semibold, color: colors.textSecondary }}>Delivery Address</div>
            <input style={inputStyle} placeholder="Full name" value={shipping.name} onChange={e => setS('name', e.target.value)} />
            <input style={inputStyle} placeholder="Address line 1" value={shipping.address1} onChange={e => setS('address1', e.target.value)} />
            <input style={inputStyle} placeholder="Address line 2 (optional)" value={shipping.address2} onChange={e => setS('address2', e.target.value)} />
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: spacing.sm }}>
              <input style={inputStyle} placeholder="Town / City" value={shipping.city} onChange={e => setS('city', e.target.value)} />
              <input style={inputStyle} placeholder="Postcode" value={shipping.zip} onChange={e => setS('zip', e.target.value)} />
            </div>
            <input style={inputStyle} placeholder="Phone (optional)" value={shipping.phone} onChange={e => setS('phone', e.target.value)} />
          </div>
        )}

        {order.printful_order_id && (
          <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: radius.md, padding: spacing.sm, fontSize: typography.fontSizes.sm }}>
            <div style={{ fontWeight: typography.fontWeights.semibold, color: '#15803d', marginBottom: 4 }}>Printful</div>
            <div><span style={{ color: colors.textMuted }}>Order ID: </span>#{order.printful_order_id}</div>
            {order.printful_order_status && (
              <div><span style={{ color: colors.textMuted }}>Status: </span><strong>{order.printful_order_status}</strong></div>
            )}
            {order.printful_tracking_url && (
              <div><span style={{ color: colors.textMuted }}>Tracking: </span>
                <a href={order.printful_tracking_url} target="_blank" rel="noreferrer" style={{ color: '#15803d' }}>
                  Track shipment
                </a>
              </div>
            )}
          </div>
        )}

        {order.notes && (
          <div style={{ background: colors.background, borderRadius: radius.md, padding: spacing.sm, fontSize: typography.fontSizes.sm }}>
            <span style={{ color: colors.textMuted }}>Customer note: </span>{order.notes}
          </div>
        )}

        <div>
          <label style={{ display: 'block', fontSize: typography.fontSizes.sm, fontWeight: typography.fontWeights.medium, color: colors.textSecondary, marginBottom: 4 }}>Status</label>
          <select style={selectStyle} value={status} onChange={e => { setStatus(e.target.value); setConfirmMismatch(false); }}>
            <option value="pending">Pending</option>
            <option value="payment_sent">Payment Sent</option>
            <option value="confirmed">Confirmed (payment received)</option>
            <option value="fulfilled">Fulfilled (dispatched / delivered)</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>

        {confirmingPayment && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.sm, background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: radius.md, padding: spacing.sm }}>
            <div style={{ fontSize: typography.fontSizes.sm, fontWeight: typography.fontWeights.semibold, color: '#1d4ed8' }}>Confirm Payment Received</div>
            <p style={{ fontSize: typography.fontSizes.xs, color: colors.textMuted, margin: 0 }}>
              Order total: {fmt(order.total_amount)}. Check the Tide account before confirming.
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: spacing.sm }}>
              <div>
                <label style={{ display: 'block', fontSize: typography.fontSizes.xs, color: colors.textSecondary, marginBottom: 4 }}>Amount Received (£) *</label>
                <input style={inputStyle} type="number" step="0.01" min="0.01" value={paidAmount}
                  onChange={e => { setPaidAmount(e.target.value); setConfirmMismatch(false); }} />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: typography.fontSizes.xs, color: colors.textSecondary, marginBottom: 4 }}>Bank Reference *</label>
                <input style={inputStyle} value={paymentRef} placeholder="From Tide statement"
                  onChange={e => setPaymentRef(e.target.value)} />
              </div>
            </div>
            {confirmMismatch && (
              <div style={{ fontSize: typography.fontSizes.xs, color: '#92400e', background: '#fef3c7', borderRadius: radius.sm, padding: '6px 8px' }}>
                Amount doesn't match the order total. Saving again will record it anyway as a confirmed mismatch.
              </div>
            )}
          </div>
        )}

        <div>
          <label style={{ display: 'block', fontSize: typography.fontSizes.sm, fontWeight: typography.fontWeights.medium, color: colors.textSecondary, marginBottom: 4 }}>Internal Notes</label>
          <textarea style={{ ...textareaStyle, minHeight: 60 }} value={adminNotes} onChange={e => setAdminNotes(e.target.value)} />
        </div>
      </div>
    </StandardModal>
  );
}

// ── Printful Config Panel ──────────────────────────────────────────────────────

function PrintfulConfigPanel({ pfConfig, saving, onSave, onRegisterWebhook }) {
  const [apiKey,   setApiKey]   = useState('');
  const [showKey,  setShowKey]  = useState(false);
  const [stores,   setStores]   = useState([]);
  const [storesLoading, setStoresLoading] = useState(false);
  const [storeId,  setStoreId]  = useState(pfConfig?.store_id || '');
  const [webhookRegistering, setWebhookRegistering] = useState(false);

  // Keep storeId in sync when pfConfig loads
  useEffect(() => { setStoreId(pfConfig?.store_id || ''); }, [pfConfig?.store_id]);

  const loadStores = () => {
    setStoresLoading(true);
    axiosClient.get('/printful/stores')
      .then(r => setStores(r.data?.stores || []))
      .catch(err => {
        const detail = err?.response?.data?.detail || err?.message || 'Failed to load stores';
        setStores([{ _error: detail }]);
      })
      .finally(() => setStoresLoading(false));
  };

  const handleSaveKey = (e) => {
    e.preventDefault();
    onSave({ api_key: apiKey });
    setApiKey('');
  };

  const handleSaveStore = () => {
    onSave({ store_id: storeId || null });
  };

  const handleRegister = async () => {
    setWebhookRegistering(true);
    try { await onRegisterWebhook(); } finally { setWebhookRegistering(false); }
  };

  return (
    <div style={{ maxWidth: 620 }}>
      <div style={{ fontSize: typography.fontSizes.lg, fontWeight: typography.fontWeights.semibold, color: colors.textPrimary, marginBottom: 4 }}>
        Printful (Print-on-Demand)
      </div>
      <div style={{ fontSize: typography.fontSizes.sm, color: colors.textMuted, marginBottom: spacing.lg }}>
        Connect your Printful store to automatically fulfil physical product orders.
        Your API key is stored securely in the database and never returned in full.
      </div>

      {/* Key status */}
      <div style={{ marginBottom: spacing.md, padding: spacing.sm, background: colors.background, borderRadius: 8, fontSize: typography.fontSizes.sm }}>
        {pfConfig?.api_key_set ? (
          <span style={{ color: '#15803d', fontWeight: 600 }}>
            ✓ API key is set
            {pfConfig.api_key_hint ? <span style={{ color: colors.textMuted, fontWeight: 400 }}> — {pfConfig.api_key_hint}</span> : ''}
            {pfConfig.source === 'env' && <span style={{ color: colors.textMuted, fontWeight: 400 }}> (from server env var)</span>}
          </span>
        ) : (
          <span style={{ color: '#b45309', fontWeight: 600 }}>⚠ No API key configured</span>
        )}
      </div>

      {/* API key form */}
      <form onSubmit={handleSaveKey} style={{ display: 'flex', flexDirection: 'column', gap: spacing.md }}>
        <div>
          <label style={{ display: 'block', fontSize: typography.fontSizes.sm, fontWeight: typography.fontWeights.medium, color: colors.textSecondary, marginBottom: 4 }}>
            {pfConfig?.api_key_set ? 'Replace API Key' : 'Printful API Key'}
          </label>
          <div style={{ display: 'flex', gap: spacing.sm }}>
            <input
              style={{ ...inputStyle, flex: 1, fontFamily: showKey ? 'inherit' : 'monospace', letterSpacing: showKey ? 'normal' : 2 }}
              type={showKey ? 'text' : 'password'}
              value={apiKey}
              onChange={e => setApiKey(e.target.value)}
              placeholder={pfConfig?.api_key_set ? 'Enter new key to replace…' : 'Paste your Printful API key…'}
              autoComplete="off"
            />
            <Btn variant="ghost" size="md" type="button" onClick={() => setShowKey(s => !s)}>
              {showKey ? 'Hide' : 'Show'}
            </Btn>
          </div>
          <div style={{ fontSize: typography.fontSizes.xs, color: colors.textMuted, marginTop: 4 }}>
            Find your key at <strong>printful.com → Settings → Stores → API</strong>
          </div>
        </div>
        <div style={{ display: 'flex', gap: spacing.sm }}>
          <Btn type="submit" variant="primary" size="md" loading={saving} disabled={!apiKey.trim()}>
            Save API Key
          </Btn>
          {pfConfig?.api_key_set && (
            <Btn type="button" variant="danger" size="md"
              onClick={() => { if (window.confirm('Clear the Printful API key?')) onSave({ api_key: '' }); }}>
              Clear Key
            </Btn>
          )}
        </div>
      </form>

      {/* Store selector — shown once key is set */}
      {pfConfig?.api_key_set && (
        <div style={{ marginTop: spacing.xl, paddingTop: spacing.lg, borderTop: `1px solid ${colors.border}` }}>
          <div style={{ fontSize: typography.fontSizes.md, fontWeight: typography.fontWeights.semibold, color: colors.textPrimary, marginBottom: 4 }}>
            Store
          </div>
          <div style={{ fontSize: typography.fontSizes.sm, color: colors.textMuted, marginBottom: spacing.md }}>
            If your Printful account has multiple stores, select which one to use.
            Single-store accounts can leave this blank.
          </div>
          <div style={{ display: 'flex', gap: spacing.sm, alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: 200 }}>
              {stores.length > 0 && !stores[0]?._error ? (
                <select style={selectStyle} value={storeId} onChange={e => setStoreId(e.target.value)}>
                  <option value="">— Any / single store —</option>
                  {stores.map(s => (
                    <option key={s.id} value={s.id}>{s.name} (ID: {s.id})</option>
                  ))}
                </select>
              ) : (
                <input
                  style={inputStyle}
                  type="number"
                  placeholder="Store ID (leave blank for single-store)"
                  value={storeId}
                  onChange={e => setStoreId(e.target.value)}
                />
              )}
              {stores[0]?._error && (
                <div style={{ fontSize: typography.fontSizes.xs, color: colors.danger, marginTop: 4 }}>{stores[0]._error}</div>
              )}
            </div>
            <Btn variant="secondary" size="md" onClick={loadStores} loading={storesLoading}>
              {stores.length ? 'Refresh Stores' : 'Load Stores'}
            </Btn>
            <Btn variant="primary" size="md" onClick={handleSaveStore} loading={saving}>
              Save Store
            </Btn>
          </div>
          {pfConfig?.store_id && (
            <div style={{ fontSize: typography.fontSizes.xs, color: '#15803d', marginTop: 6 }}>
              ✓ Store ID {pfConfig.store_id} is saved
            </div>
          )}
        </div>
      )}

      {/* Webhook */}
      {pfConfig?.api_key_set && (
        <div style={{ marginTop: spacing.xl, paddingTop: spacing.lg, borderTop: `1px solid ${colors.border}` }}>
          <div style={{ fontSize: typography.fontSizes.md, fontWeight: typography.fontWeights.semibold, color: colors.textPrimary, marginBottom: 4 }}>
            Webhook
          </div>
          <div style={{ fontSize: typography.fontSizes.sm, color: colors.textMuted, marginBottom: spacing.md }}>
            Register the webhook so Printful can notify BOSS when orders ship.
            URL: <code style={{ background: colors.background, padding: '1px 6px', borderRadius: 4, fontSize: '0.85em' }}>https://isoboss.councilforstandards.org/api/printful/webhook</code>
          </div>
          <Btn variant="secondary" size="md" onClick={handleRegister} loading={webhookRegistering}>
            Register Webhook with Printful
          </Btn>
        </div>
      )}
    </div>
  );
}

// ── Main Component ─────────────────────────────────────────────────────────────

const TABS = [
  { key: 'items',        label: 'Item Catalogue' },
  { key: 'orders',       label: 'Orders' },
  { key: 'config',       label: 'Bank Settings' },
  { key: 'integrations', label: 'Integrations' },
];

const ITEM_COLS = [
  { key: 'title',     label: 'Title',  render: (v, row) => (
    <span>{TYPE_ICONS[row.item_type]} {v}</span>
  )},
  { key: 'item_type', label: 'Type',   render: v => TYPE_LABELS[v] || v },
  { key: 'price',     label: 'Price',  render: (v, row) => row.is_free ? <span style={{ color: '#10b981', fontWeight: 600 }}>Free</span> : `£${Number(v).toFixed(2)}` },
  { key: 'status',    label: 'Status', render: v => <StatusChip status={v} map={STATUS_LABELS} colorMap={ITEM_STATUS_COLORS} /> },
  { key: 'stock_quantity', label: 'Stock', render: v => v === null || v === undefined ? '∞' : v },
  { key: 'requires_accreditation', label: 'Gated', render: v => v ? <span style={{ color: '#f59e0b', fontWeight: 600 }}>Accred.</span> : '—' },
];

const ORDER_COLS = [
  { key: 'order_number',  label: 'Order #' },
  { key: 'customer_name', label: 'Customer' },
  { key: 'total_amount',  label: 'Total',  render: v => fmt(v) },
  { key: 'status',        label: 'Status', render: v => <StatusChip status={v} map={ORDER_STATUS_LABELS} colorMap={ORDER_STATUS_COLORS} /> },
  { key: 'created_at',    label: 'Placed', render: v => new Date(v).toLocaleDateString() },
];

export default function ShopManagement() {
  const [activeTab, setActiveTab] = useState('items');

  const [items,   setItems]   = useState([]);
  const [orders,  setOrders]  = useState([]);
  const [config,  setConfig]  = useState(null);
  const [loading, setLoading] = useState(true);

  const [showItemModal,  setShowItemModal]  = useState(false);
  const [editingItem,    setEditingItem]    = useState(null);
  const [showOrderModal, setShowOrderModal] = useState(false);
  const [selectedOrder,  setSelectedOrder]  = useState(null);
  const [itemSaving,     setItemSaving]     = useState(false);
  const [configSaving,   setConfigSaving]   = useState(false);
  const [pfSyncing,      setPfSyncing]      = useState(false);
  const [pfConfig,       setPfConfig]       = useState(null);
  const [pfConfigSaving, setPfConfigSaving] = useState(false);

  const [msg, setMsg] = useState(null);
  const flash = (text, type = 'success') => { setMsg({ text, type }); setTimeout(() => setMsg(null), 4000); };

  const [deleteTarget, setDeleteTarget] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [itemsRes, ordersRes, cfgRes, pfCfgRes] = await Promise.all([
        axiosClient.get('/shop/items'),
        axiosClient.get('/shop/orders'),
        axiosClient.get('/shop/config'),
        axiosClient.get('/printful/config').catch(() => ({ data: null })),
      ]);
      setItems(itemsRes.data);
      setOrders(ordersRes.data);
      setConfig(cfgRes.data);
      setPfConfig(pfCfgRes.data);
    } catch {
      flash('Failed to load shop data.', 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const saveItem = async (payload) => {
    setItemSaving(true);
    try {
      if (editingItem) {
        const res = await axiosClient.put(`/shop/items/${editingItem.id}`, payload);
        setItems(prev => prev.map(i => i.id === editingItem.id ? res.data : i));
        flash('Item updated.');
      } else {
        const res = await axiosClient.post('/shop/items', payload);
        setItems(prev => [res.data, ...prev]);
        flash('Item created.');
      }
      setShowItemModal(false);
      setEditingItem(null);
    } catch {
      flash('Failed to save item.', 'error');
    } finally {
      setItemSaving(false);
    }
  };

  const deleteItem = async () => {
    if (!deleteTarget) return;
    try {
      await axiosClient.delete(`/shop/items/${deleteTarget.id}`);
      setItems(prev => prev.filter(i => i.id !== deleteTarget.id));
      flash('Item deleted.');
    } catch {
      flash('Failed to delete item.', 'error');
    } finally {
      setDeleteTarget(null);
    }
  };

  const syncPrintful = async () => {
    setPfSyncing(true);
    try {
      const res = await axiosClient.post('/printful/sync');
      flash(`Printful sync complete — ${res.data.created} created, ${res.data.updated} updated.`);
      load();
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || 'Unknown error';
      const isApiStoreError = detail.includes('Manual Order') || detail.includes('API platform');
      flash(
        isApiStoreError
          ? 'Sync requires a "Manual Order / API" Printful store. In Printful go to Stores → Add new store → Manual Order / API, then update your API key here.'
          : `Printful sync failed: ${detail}`,
        'error'
      );
    } finally {
      setPfSyncing(false);
    }
  };

  const saveConfig = async (payload) => {
    setConfigSaving(true);
    try {
      const res = await axiosClient.patch('/shop/config', payload);
      setConfig(res.data);
      flash('Bank details saved.');
    } catch {
      flash('Failed to save bank details.', 'error');
    } finally {
      setConfigSaving(false);
    }
  };

  return (
    <div style={{ maxWidth: 1100, margin: '0 auto', padding: spacing.lg }}>
      <PageHeader
        title="Shop Management"
        subtitle="Manage items, orders, and payment settings for The ICSC Store"
        actions={
          activeTab === 'items' && (
            <div style={{ display: 'flex', gap: spacing.sm }}>
              <Btn variant="secondary" size="md" onClick={syncPrintful} loading={pfSyncing}>
                Sync Printful
              </Btn>
              <Btn variant="primary" size="md" onClick={() => { setEditingItem(null); setShowItemModal(true); }}>
                + Add Item
              </Btn>
            </div>
          )
        }
      />

      {msg && <Alert type={msg.type} message={msg.text} />}

      <Tabs
        tabs={TABS}
        value={activeTab}
        onChange={setActiveTab}
        style={{ marginBottom: spacing.lg }}
      />

      {/* ── Items tab ── */}
      {activeTab === 'items' && (
        <Card>
          <DataTable
            columns={[
              ...ITEM_COLS,
              {
                key: '_actions',
                label: '',
                render: (_, row) => (
                  <div style={{ display: 'flex', gap: spacing.sm }}>
                    <Btn variant="ghost" size="sm" onClick={() => { setEditingItem(row); setShowItemModal(true); }}>Edit</Btn>
                    <Btn variant="danger"  size="sm" onClick={() => setDeleteTarget(row)}>Delete</Btn>
                  </div>
                ),
              },
            ]}
            rows={items}
            keyField="id"
            loading={loading}
            emptyMessage="No items yet — click '+ Add Item' to create your first."
          />
        </Card>
      )}

      {/* ── Orders tab ── */}
      {activeTab === 'orders' && (
        <Card>
          <DataTable
            columns={[
              ...ORDER_COLS,
              {
                key: '_actions',
                label: '',
                render: (_, row) => (
                  <Btn variant="ghost" size="sm" onClick={() => { setSelectedOrder(row); setShowOrderModal(true); }}>
                    Manage
                  </Btn>
                ),
              },
            ]}
            rows={orders}
            keyField="id"
            loading={loading}
            emptyMessage="No orders yet."
          />
        </Card>
      )}

      {/* ── Bank config tab ── */}
      {activeTab === 'config' && (
        <Card>
          <div style={{ maxWidth: 600 }}>
            <div style={{ marginBottom: spacing.lg }}>
              <div style={{ fontSize: typography.fontSizes.lg, fontWeight: typography.fontWeights.semibold, color: colors.textPrimary, marginBottom: 4 }}>
                Bank Transfer Details
              </div>
              <div style={{ fontSize: typography.fontSizes.sm, color: colors.textMuted }}>
                These details are shown to customers at checkout so they can pay by bank transfer to your TIDE account.
              </div>
            </div>
            {config && (
              <BankConfigForm config={config} onSave={saveConfig} saving={configSaving} />
            )}
          </div>
        </Card>
      )}

      {/* ── Integrations tab ── */}
      {activeTab === 'integrations' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.lg }}>
          <Card>
            <PrintfulConfigPanel
              pfConfig={pfConfig}
              saving={pfConfigSaving}
              onSave={async (patch) => {
                setPfConfigSaving(true);
                try {
                  const res = await axiosClient.patch('/printful/config', patch);
                  setPfConfig(res.data);
                  if ('api_key' in patch) flash(patch.api_key ? 'Printful API key saved.' : 'Printful API key cleared.');
                  if ('store_id' in patch) flash(patch.store_id ? `Store ID ${patch.store_id} saved.` : 'Store selection cleared.');
                } catch (err) {
                  flash(err?.response?.data?.detail || 'Failed to save Printful config.', 'error');
                } finally {
                  setPfConfigSaving(false);
                }
              }}
              onRegisterWebhook={async () => {
                const url = `https://isoboss.councilforstandards.org/api/printful/webhook`;
                try {
                  await axiosClient.post('/printful/webhook/register', { url });
                  flash('Webhook registered with Printful.');
                } catch (err) {
                  const detail = err?.response?.data?.detail || err?.message || 'Failed to register webhook.';
                  flash(`Webhook registration failed: ${detail}`, 'error');
                }
              }}
            />
          </Card>
        </div>
      )}

      {/* ── Item create/edit modal ── */}
      {showItemModal && (
        <StandardModal
          title={editingItem ? `Edit: ${editingItem.title}` : 'Add New Item'}
          onClose={() => { setShowItemModal(false); setEditingItem(null); }}
          width={600}
          footer={
            <div style={{ display: 'flex', gap: spacing.sm, justifyContent: 'flex-end' }}>
              <Btn variant="secondary" size="md" onClick={() => { setShowItemModal(false); setEditingItem(null); }}>Cancel</Btn>
              <Btn type="submit" form="item-form" variant="primary" size="md" loading={itemSaving}>
                {editingItem ? 'Save Changes' : 'Create Item'}
              </Btn>
            </div>
          }
        >
          <ItemForm
            initial={editingItem || {}}
            onSave={saveItem}
            onCancel={() => { setShowItemModal(false); setEditingItem(null); }}
            saving={itemSaving}
          />
        </StandardModal>
      )}

      {/* ── Delete confirm modal ── */}
      {deleteTarget && (
        <StandardModal
          title="Delete Item"
          onClose={() => setDeleteTarget(null)}
          danger
          width={420}
          footer={
            <div style={{ display: 'flex', gap: spacing.sm, justifyContent: 'flex-end' }}>
              <Btn variant="secondary" size="md" onClick={() => setDeleteTarget(null)}>Cancel</Btn>
              <Btn variant="danger"    size="md" onClick={deleteItem}>Delete</Btn>
            </div>
          }
        >
          <p style={{ margin: 0, fontSize: typography.fontSizes.md, color: colors.textPrimary }}>
            Are you sure you want to delete <strong>{deleteTarget.title}</strong>? This cannot be undone.
          </p>
        </StandardModal>
      )}

      {/* ── Order detail modal ── */}
      {showOrderModal && selectedOrder && (
        <OrderDetailModal
          order={selectedOrder}
          onClose={() => { setShowOrderModal(false); setSelectedOrder(null); }}
          onStatusChange={(updated) => {
            setOrders(prev => prev.map(o => o.id === updated.id ? updated : o));
            setSelectedOrder(updated);
          }}
        />
      )}
    </div>
  );
}
